import { type FC } from "react";

import { TagIcon } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId, SurfaceType } from "../../../gql-generated/types";
import { Tags, TagsContainer } from "../../../lens-tags/components";
import { FormModal } from "../../../shared/components";
import { MAX_SPECTACLES_TAGS } from "../../../submit/components/constants";
import { useLensChangeTracker, useSpectaclesTagsForm } from "../../hooks";
import { FieldLabel } from "../shared";

import { SidebarModalHeading, SidebarModalInfo } from "./LensSidebar.styles";
import { formCSS } from "./LensTags.styles";
import { SidebarFooterButtons } from "./SidebarFooterButtons";
import { SidebarPanel } from "./SidebarPanel";
import { SpectaclesTagsDescription, messages } from "./messages.generated";

interface SpectaclesTagsSideBarProps {
    disabled: boolean;
    tags: readonly string[];
    lensId: string;
}

export const SpectaclesTagsSideBar: FC<SpectaclesTagsSideBarProps> = ({ disabled, tags, lensId }) => {
    const intl = useIntl();
    const { configs } = useFeatureGates();
    const isTrendingTagsEnabled = configs[CofConfigId.MyLensesTrendingTags].enabled;
    const { openTagsModal, tagsModalProps, applyTags, updateLensLoading, addTag, removeTag, tagsList } =
        useSpectaclesTagsForm(tags, lensId);
    const { hasFieldChanged, spectaclesTags: editedSpectaclesTags } = useLensChangeTracker(lensId);

    // Use edited tags (optimistic UI) with fallback to server tags
    const displayTags =
        (editedSpectaclesTags && editedSpectaclesTags.length > 0) ?? hasFieldChanged("spectaclesTags")
            ? editedSpectaclesTags ?? []
            : tags;

    return (
        <>
            <SidebarPanel
                dataTestIdPrefix="spectacles-tags"
                label={
                    <FieldLabel hasChanges={hasFieldChanged("spectaclesTags")}>
                        {intl.formatMessage(messages.spectaclesTagsLabel, {
                            curr: displayTags.length,
                            limit: MAX_SPECTACLES_TAGS,
                        })}
                    </FieldLabel>
                }
                actionLabel={intl.formatMessage(messages.spectaclesTagsActionLabel)}
                description={<SpectaclesTagsDescription limit={MAX_SPECTACLES_TAGS} />}
                tooltipContent={<SpectaclesTagsDescription limit={MAX_SPECTACLES_TAGS} />}
                Icon={TagIcon}
                showModal={openTagsModal}
                disabled={disabled}
            >
                {displayTags.length > 0 ? <TagsContainer tagsList={displayTags} unremovable={true} /> : null}
            </SidebarPanel>
            <FormModal
                footer={
                    <SidebarFooterButtons
                        dataTestIdPrefix="tags"
                        onCancel={tagsModalProps.onCancel}
                        apply={applyTags}
                        applyLoading={updateLensLoading}
                    />
                }
                modalProps={tagsModalProps}
                {...(isTrendingTagsEnabled && {
                    width: 800,
                    formClassName: formCSS,
                })}
            >
                <SidebarModalHeading>Manage Lens Tags</SidebarModalHeading>
                <SidebarModalInfo>Select up to {MAX_SPECTACLES_TAGS} tags.</SidebarModalInfo>
                <Tags
                    lensSurfaceType={SurfaceType.SurfaceTypeSpectacles}
                    addTag={addTag}
                    removeTag={removeTag}
                    tags={tagsList}
                    placeholder="Tag your Lens"
                />
            </FormModal>
        </>
    );
};
